import React, { useEffect, useMemo, useState } from 'react';
import {
  Bank, Buildings, CalendarBlank, Clock, Coins, EnvelopeSimple, FileText, Globe, Hash, IdentificationCard, Lightning, MagnifyingGlass, MapPin,
  MapTrifold, NotePencil, Percent, Phone, ShieldCheck, Signpost, Siren, Star, User, Users,
} from '@phosphor-icons/react';
import {
  Chip, FeldRaster, LivePreviewCard, PersonAvatar, SchalterZeile, SegmentedControl, StepForm, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { normalisiereSuche } from '../../../../lib/immobilien/suche';
import type { KategorieRoh } from '../../../../lib/dienstleister/liste';
import {
  bevorzugteImGewerk, DL_ARTEN, dlPayload, feldFehler, hinweis, LEERER_DL_ENTWURF, nachweisZeilen, schritteErledigt, zahl,
  type BestandDl, type DlArt, type Einordnung, type NeuerDlEntwurf,
} from '../../../../lib/dienstleister/neuerDienstleister';
import { legeDienstleisterAn } from '../../../../lib/dienstleister/aktionen';

const KACHEL = (an: boolean): React.CSSProperties => ({
  borderRadius: 14, padding: '10px 12px', gap: 2, border: 'none', color: 'var(--ink)', textAlign: 'left',
  background: an ? 'var(--acc-soft)' : 'var(--bg)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)',
});

/**
 * Rückseite der Drehkarte „Neuer Dienstleister“ (Handoff Dienstleister §8, Screens 10/11/37):
 * Firma & Kontakt · Gewerke & Kategorien · Konditionen & Nachweise, rechts die Live-Vorschau.
 */
export function NeuerDienstleisterFormular({ kategorien, bestand, dlJeKategorie, heute, onGeaendert, onGespeichert }: {
  kategorien: readonly KategorieRoh[];
  bestand: readonly BestandDl[];
  dlJeKategorie: ReadonlyMap<string, number>;
  heute: string;
  onGeaendert: (geaendert: boolean) => void;
  onGespeichert: (id: string, meldung: string) => void;
}) {
  const [e, setE] = useState<NeuerDlEntwurf>(LEERER_DL_ENTWURF);
  const [suche, setSuche] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof NeuerDlEntwurf>(k: K) => (v: NeuerDlEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));

  useEffect(() => { onGeaendert(JSON.stringify(e) !== JSON.stringify(LEERER_DL_ENTWURF)); }, [e, onGeaendert]);

  const [s1, s2, s3] = schritteErledigt(e);
  const h = hinweis(e);
  const fehler = feldFehler(e);
  const konflikt = useMemo(() => bevorzugteImGewerk(e.kategorieIds, bestand), [e.kategorieIds, bestand]);
  const katName = new Map(kategorien.map(k => [k.id, k.name]));
  const gewaehlteNamen = e.kategorieIds.map(id => katName.get(id) ?? '').filter(Boolean);
  const aktive = kategorien.filter(k => k.aktiv);
  const q = normalisiereSuche(suche.trim());
  const sichtbar = q ? aktive.filter(k => normalisiereSuche(k.name).includes(q)) : aktive;
  const schalteKat = (id: string) => setE(alt => ({ ...alt, kategorieIds: alt.kategorieIds.includes(id) ? alt.kategorieIds.filter(x => x !== id) : [...alt.kategorieIds, id] }));
  const stundensatz = zahl(e.stundensatz);

  const speichere = async () => {
    if (!h.bereit) return;
    setSpeichert(true);
    try {
      const r = await legeDienstleisterAn(dlPayload(e, bestand, heute), e.kategorieIds, nachweisZeilen(e));
      onGespeichert(r.id, [`${e.name.trim()} angelegt · ${gewaehlteNamen.join(', ')}`, ...r.warnungen].join(' · '));
    } catch (err) {
      zeige(`Nicht gespeichert: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setSpeichert(false);
    }
  };

  const schritte = [
    {
      titel: 'Firma & Kontakt',
      untertitel: 'Name, Ansprechpartner, Anschrift',
      erledigt: s1,
      inhalt: (
        <>
          <SegmentedControl<DlArt> ariaLabel="Art" hoehe={34} wert={e.art} onChange={setze('art')} segmente={DL_ARTEN.map(a => ({ key: a.key, label: a.label }))} className="self-start" />
          <FeldRaster minBreite={170}>
            <TextFeld label="Name *" icon={Buildings} wert={e.name} onChange={setze('name')} placeholder="z. B. Sanitär Lahn GmbH" autoComplete="off" />
            <TextFeld label="Ansprechpartner" icon={User} wert={e.ansprechpartner} onChange={setze('ansprechpartner')} placeholder="Vor- und Nachname" autoComplete="off" />
            <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} placeholder="info@firma.de" autoComplete="off" fehler={fehler.email} />
            <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} placeholder="06421 …" autoComplete="off" />
            <TextFeld label="Notdienst-Telefon" icon={Siren} type="tel" wert={e.notdienstTelefon} onChange={setze('notdienstTelefon')} placeholder="optional, 24/7" autoComplete="off" />
            <TextFeld label="Website" icon={Globe} wert={e.website} onChange={setze('website')} placeholder="www.firma.de" autoComplete="off" />
            <TextFeld label="Straße" icon={Signpost} wert={e.strasse} onChange={setze('strasse')} placeholder="Industriestraße" autoComplete="off" />
            <TextFeld label="Nr." icon={Hash} wert={e.hausnummer} onChange={setze('hausnummer')} placeholder="1" autoComplete="off" />
            <TextFeld label="PLZ" icon={MapPin} inputMode="numeric" wert={e.plz} onChange={setze('plz')} placeholder="35037" autoComplete="off" fehler={fehler.plz} />
            <TextFeld label="Stadt" icon={MapTrifold} wert={e.stadt} onChange={setze('stadt')} placeholder="Marburg" autoComplete="off" />
          </FeldRaster>
        </>
      ),
    },
    {
      titel: 'Gewerke & Kategorien',
      untertitel: 'Mehrfachauswahl – steuert die Ticket-Zuweisung',
      erledigt: s2,
      inhalt: (
        <>
          <TextFeld label="Kategorie suchen" icon={MagnifyingGlass} wert={suche} onChange={setSuche} placeholder="Kategorie suchen …" autoComplete="off" type="search" />
          <div role="group" aria-label="Kategorien" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
            {sichtbar.map(k => {
              const an = e.kategorieIds.includes(k.id);
              return (
                <button key={k.id} type="button" aria-pressed={an} onClick={() => schalteKat(k.id)} className="flex flex-col cursor-pointer min-w-0" style={KACHEL(an)}>
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{k.name}</span>
                  <span className="truncate" style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink3)' }}>
                    {k.ist_handwerk ? 'Handwerk' : 'Dienstleistung'} · {dlJeKategorie.get(k.id) ?? 0} vorhanden
                  </span>
                </button>
              );
            })}
            {sichtbar.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Kategorie gefunden – unter „Kategorien &amp; Regeln“ anlegen.</span>}
          </div>
          {konflikt.length > 0 && (
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Für diese Gewerke gibt es schon bevorzugte Dienstleister – wie einordnen?</span>
              <div role="radiogroup" aria-label="Einordnung" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {([['bevorzugt', Star, 'Ebenfalls bevorzugt', `Wird bei Tickets gleichrangig mit ${konflikt.map(k => k.name).join(', ')} vorgeschlagen.`],
                  ['vertretung', Users, 'Als Vertretung', 'Wird nur vorgeschlagen, wenn der bevorzugte Dienstleister nicht verfügbar ist.']] as const).map(([key, IconKomp, titel, text]) => {
                  const an = e.einordnung === key;
                  return (
                    <button key={key} type="button" role="radio" aria-checked={an} onClick={() => setze('einordnung')(key as Einordnung)}
                      className="flex items-start cursor-pointer min-w-0" style={{ ...KACHEL(an), gap: 10, padding: 12 }}>
                      <IconKomp size={18} weight="fill" color="var(--acc)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{titel}</span>
                        <span style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.4 }}>{text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      titel: 'Konditionen & Nachweise',
      untertitel: 'Preise, Beauftragung, Bank, § 48b, Haftpflicht',
      erledigt: s3,
      inhalt: (
        <>
          <FeldRaster minBreite={170}>
            <TextFeld label="Stundensatz" icon={Coins} inputMode="decimal" suffix="€ / h" wert={e.stundensatz} onChange={setze('stundensatz')} placeholder="0" fehler={fehler.stundensatz} />
            <TextFeld label="Anfahrtspauschale" icon={Coins} inputMode="decimal" suffix="€" wert={e.anfahrt} onChange={setze('anfahrt')} placeholder="0" fehler={fehler.anfahrt} />
            <TextFeld label="Notdienstzuschlag" icon={Percent} inputMode="decimal" suffix="%" wert={e.notdienstzuschlag} onChange={setze('notdienstzuschlag')} placeholder="0" fehler={fehler.notdienstzuschlag} />
            <TextFeld label="Zahlungsziel" icon={Clock} inputMode="numeric" suffix="Tage" wert={e.zahlungsziel} onChange={setze('zahlungsziel')} placeholder="14" fehler={fehler.zahlungsziel} />
            <TextFeld label="IBAN" icon={Bank} wert={e.iban} onChange={setze('iban')} placeholder="DE00 …" autoComplete="off" fehler={fehler.iban} />
            <TextFeld label="USt-IdNr." icon={IdentificationCard} wert={e.ustId} onChange={setze('ustId')} placeholder="DE123456789" autoComplete="off" />
            <TextFeld label="Freistellung § 48b gültig bis" icon={ShieldCheck} type="date" wert={e.freistellungBis} onChange={setze('freistellungBis')} />
            <TextFeld label="Betriebshaftpflicht gültig bis" icon={CalendarBlank} type="date" wert={e.haftpflichtBis} onChange={setze('haftpflichtBis')} />
            <TextFeld label="Notizen" icon={NotePencil} voll wert={e.notizen} onChange={setze('notizen')} placeholder="Besonderheiten, Schlüssel, Zugang …" />
          </FeldRaster>
          <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Freigabegrenze</span>
            <Stepper label="Freigabegrenze" wert={e.freigabegrenze} onChange={setze('freigabegrenze')} min={0} max={5000} schritt={100} einheit="€" />
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>bis {eur(e.freigabegrenze, { nachkomma: 0 })} ohne Rückfrage</span>
          </div>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Beauftragung</span>
            <SegmentedControl<'direkt' | 'angebot'> ariaLabel="Beauftragung" hoehe={34} className="self-start" wert={e.modus} onChange={setze('modus')}
              segmente={[{ key: 'direkt', label: 'Direkt beauftragen', icon: Lightning }, { key: 'angebot', label: 'Erst Angebot', icon: FileText }]} />
          </div>
          <SchalterZeile an={e.notfall} onChange={setze('notfall')} icon={Siren} label="Notfallkontakt (24/7 erreichbar)" hintergrundAn="var(--bad-soft)" />
        </>
      ),
    },
  ];

  const name = e.name.trim() || 'Neuer Dienstleister';
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <StepForm
        schritte={schritte}
        hinweis={h.text}
        bereit={h.bereit}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel={speichert ? 'Wird angelegt …' : 'Dienstleister anlegen'}
        vorschau={(
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <PersonAvatar text={e.name.trim() ? initialen(name) : '?'} ton={e.notfall ? 'bad' : kategorien.some(k => e.kategorieIds.includes(k.id) && k.ist_handwerk) ? 'acc' : 'info'} />
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{name}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{gewaehlteNamen.join(', ') || 'Keine Kategorie gewählt'}</span>
              </div>
            </div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              <Chip ton="ok">Aktiv</Chip>
              {e.notfall && <Chip ton="bad">Notfall 24/7</Chip>}
              {konflikt.length > 0 ? <Chip ton="neutral">{e.einordnung === 'bevorzugt' ? 'bevorzugt' : 'Vertretung'}</Chip> : e.kategorieIds.length > 0 && <Chip ton="acc">bevorzugt</Chip>}
            </div>
            <div className="flex flex-col" style={{ gap: 8, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 13 }}>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Stundensatz</span><b className="immo-zahl">{stundensatz !== null && !Number.isNaN(stundensatz) ? `${eur(stundensatz)} / h` : '–'}</b></div>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Freigabegrenze</span><b className="immo-zahl">bis {eur(e.freigabegrenze, { nachkomma: 0 })}</b></div>
              <div className="flex justify-between items-baseline" style={{ gap: 12 }}>
                <span style={{ color: 'var(--ink2)' }}>Beauftragung</span>
                <b className="text-right" style={{ fontSize: 15, color: 'var(--ok)' }}>{e.modus === 'angebot' ? 'Erst Angebot' : 'Direkt beauftragen'}</b>
              </div>
            </div>
          </LivePreviewCard>
        )}
      />
    </div>
  );
}
